import type { DropdownMenuContentProps } from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import type { FunctionComponent, ReactNode } from 'react';

import { Button, type ButtonProps } from '../button/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../dropdown-menu/DropdownMenu';
import { IconChevronDownSmall, IconChevronDownSmallLight } from '../icons';

export type SplitDropdownButtonProps = {
    readonly children: ReactNode;
    readonly className?: string;
    readonly menuAlign?: DropdownMenuContentProps['align'];
    readonly menuLabel: string;
    readonly primary: ButtonProps;
    readonly triggerProps?: Omit<ButtonProps, 'aria-label' | 'children' | 'iconOnly'>;
};

const containerClasses = clsx(
    'inline-flex items-stretch',
    '[&>button:first-of-type]:!rounded-r-none',
    '[&>button:last-of-type]:!rounded-l-none',
    '[&>button:last-of-type]:!px-s',
);

const primaryMainHoverClasses = clsx(
    '[&>button:first-of-type:hover+button]:bg-black-800',
    '[&>button:first-of-type:hover+button]:shadow-primary-lg',
);

const resolvesToPrimary = ({ color, variant }: Pick<ButtonProps, 'color' | 'variant'>): boolean => {
    if (variant === 'solid' || variant == null) {
        return color !== 'danger' && color !== 'muted' && color !== 'secondary';
    }

    return variant === 'default' || variant === 'primary';
};

export const SplitDropdownButton: FunctionComponent<SplitDropdownButtonProps> = ({
    children,
    className,
    menuAlign = 'end',
    menuLabel,
    primary,
    triggerProps,
}) => {
    const inheritedTriggerProps: Omit<ButtonProps, 'children'> = {
        color: primary.color,
        disabled: primary.disabled,
        size: primary.size,
        variant: primary.variant,
        ...triggerProps,
    };

    const usesLightChevron =
        inheritedTriggerProps.variant == null ||
        inheritedTriggerProps.variant === 'default' ||
        inheritedTriggerProps.variant === 'primary' ||
        inheritedTriggerProps.variant === 'solid';
    const ChevronIcon = usesLightChevron ? IconChevronDownSmallLight : IconChevronDownSmall;
    const shouldHoverTriggerWithMain = !primary.disabled && resolvesToPrimary(inheritedTriggerProps);

    return (
        <div className={clsx(containerClasses, shouldHoverTriggerWithMain && primaryMainHoverClasses, className)}>
            <Button {...primary} />
            <DropdownMenu>
                <DropdownMenuTrigger>
                    <Button aria-label={menuLabel} {...inheritedTriggerProps}>
                        <ChevronIcon />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={menuAlign}>{children}</DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
};
